import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

export interface AiTestCommand {
  id: string;
  pipeline: string;
  work: string;
  provider: string;
  field: string;
  keywords: string[];
  description: string;
}

export interface TestProviderKeyResult {
  success: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AiTestCommandsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/settings`;

  lastResult = signal<{ command: string; success: boolean; message: string } | null>(null);
  isTesting = signal(false);

  readonly commands = signal<AiTestCommand[]>([
    // ── Processing Text ──
    { id: 'proc-openai-text', pipeline: 'Processing', work: 'text', provider: 'openai', field: 'openAIKey', keywords: ['processing', 'text', 'openai', 'chat', 'gpt', 'script'], description: 'Processing text via OpenAI (GPT models)' },
    { id: 'proc-openrouter-text', pipeline: 'Processing', work: 'text', provider: 'openrouter', field: 'openRouterApiKey', keywords: ['processing', 'text', 'openrouter', 'router'], description: 'Processing text via OpenRouter' },
    { id: 'proc-gemini-text', pipeline: 'Processing', work: 'text', provider: 'gemini', field: 'geminiApiKey', keywords: ['processing', 'text', 'gemini', 'google'], description: 'Processing text via Google Gemini' },
    { id: 'proc-deepseek-text', pipeline: 'Processing', work: 'text', provider: 'deepseek', field: 'deepSeekApiKey', keywords: ['processing', 'text', 'deepseek'], description: 'Processing text via DeepSeek' },
    { id: 'proc-alibaba-text', pipeline: 'Processing', work: 'text', provider: 'alibaba', field: 'alibabaApiKey', keywords: ['processing', 'text', 'alibaba', 'qwen'], description: 'Processing text via Alibaba Qwen' },

    // ── Processing Image ──
    // (Processing pipeline has no image key directly — uses whisper/audio)

    // ── Processing Whisper ──
    { id: 'proc-whisper-stt', pipeline: 'Processing', work: 'whisper', provider: 'whisper', field: 'whisperApiKey', keywords: ['processing', 'whisper', 'speech', 'stt', 'transcribe', 'audio', 'voice'], description: 'Processing speech-to-text via Whisper' },
    { id: 'proc-groq-whisper', pipeline: 'Processing', work: 'whisper', provider: 'groq', field: 'groqApiKey', keywords: ['processing', 'whisper', 'groq', 'speech'], description: 'Processing speech-to-text via Groq Whisper' },

    // ── Processing Assets ──
    { id: 'proc-pexels', pipeline: 'Processing', work: 'assets', provider: 'pexels', field: 'pexelsApiKey', keywords: ['processing', 'pexels', 'stock', 'media', 'image', 'video'], description: 'Processing stock media via Pexels' },

    // ── Trend Text ──
    { id: 'trend-openai-text', pipeline: 'Trend', work: 'text', provider: 'openai', field: 'openAIKey', keywords: ['trend', 'text', 'openai', 'gpt', 'analysis'], description: 'Trend text analysis via OpenAI' },
    { id: 'trend-openrouter-text', pipeline: 'Trend', work: 'text', provider: 'openrouter', field: 'openRouterApiKey', keywords: ['trend', 'text', 'openrouter'], description: 'Trend text analysis via OpenRouter' },
    { id: 'trend-gemini-text', pipeline: 'Trend', work: 'text', provider: 'gemini', field: 'geminiApiKey', keywords: ['trend', 'text', 'gemini', 'google'], description: 'Trend text analysis via Gemini' },
    { id: 'trend-deepseek-text', pipeline: 'Trend', work: 'text', provider: 'deepseek', field: 'deepSeekApiKey', keywords: ['trend', 'text', 'deepseek'], description: 'Trend text analysis via DeepSeek' },
    { id: 'trend-alibaba-text', pipeline: 'Trend', work: 'text', provider: 'alibaba', field: 'alibabaApiKey', keywords: ['trend', 'text', 'alibaba', 'qwen'], description: 'Trend text analysis via Alibaba' },

    // ── Video Script ──
    { id: 'vid-script-openai', pipeline: 'VideoGenerator', work: 'script', provider: 'openai', field: 'openAiScriptApiKey', keywords: ['video', 'script', 'openai', 'gpt'], description: 'Video script via OpenAI' },
    { id: 'vid-script-gemini', pipeline: 'VideoGenerator', work: 'script', provider: 'gemini', field: 'geminiScriptApiKey', keywords: ['video', 'script', 'gemini', 'google'], description: 'Video script via Gemini' },
    { id: 'vid-script-openrouter', pipeline: 'VideoGenerator', work: 'script', provider: 'openrouter', field: 'openRouterScriptApiKey', keywords: ['video', 'script', 'openrouter'], description: 'Video script via OpenRouter' },
    { id: 'vid-script-deepseek', pipeline: 'VideoGenerator', work: 'script', provider: 'deepseek', field: 'deepSeekScriptApiKey', keywords: ['video', 'script', 'deepseek'], description: 'Video script via DeepSeek' },
    { id: 'vid-script-alibaba', pipeline: 'VideoGenerator', work: 'script', provider: 'alibaba', field: 'alibabaScriptApiKey', keywords: ['video', 'script', 'alibaba', 'qwen'], description: 'Video script via Alibaba' },

    // ── Video Image ──
    { id: 'vid-image-openai', pipeline: 'VideoGenerator', work: 'image', provider: 'openai', field: 'openAiImageApiKey', keywords: ['video', 'image', 'openai', 'dalle'], description: 'Video image generation via OpenAI DALL-E' },
    { id: 'vid-image-gemini', pipeline: 'VideoGenerator', work: 'image', provider: 'gemini', field: 'geminiImageApiKey', keywords: ['video', 'image', 'gemini', 'google', 'imagen'], description: 'Video image generation via Gemini' },
    { id: 'vid-image-stability', pipeline: 'VideoGenerator', work: 'image', provider: 'stability', field: 'stabilityApiKey', keywords: ['video', 'image', 'stability', 'stable diffusion'], description: 'Video image via Stability AI' },

    // ── Video TTS ──
    { id: 'vid-tts-openai', pipeline: 'VideoGenerator', work: 'tts', provider: 'openai', field: 'openAiTtsApiKey', keywords: ['video', 'tts', 'openai', 'voice', 'speech'], description: 'Video TTS via OpenAI' },
    { id: 'vid-tts-azure', pipeline: 'VideoGenerator', work: 'tts', provider: 'azure', field: 'azureTtsApiKey', keywords: ['video', 'tts', 'azure', 'microsoft'], description: 'Video TTS via Azure' },
    { id: 'vid-tts-elevenlabs', pipeline: 'VideoGenerator', work: 'tts', provider: 'elevenlabs', field: 'elevenLabsTtsApiKey', keywords: ['video', 'tts', 'elevenlabs', 'eleven'], description: 'Video TTS via ElevenLabs' },
    { id: 'vid-tts-cartesia', pipeline: 'VideoGenerator', work: 'tts', provider: 'cartesia', field: 'cartesiaTtsApiKey', keywords: ['video', 'tts', 'cartesia'], description: 'Video TTS via Cartesia' },

    // ── Video Music ──
    { id: 'vid-music-together', pipeline: 'VideoGenerator', work: 'music', provider: 'together', field: 'togetherMusicApiKey', keywords: ['video', 'music', 'together', 'audio'], description: 'Video music via Together AI' },

    // ── Video Gen ──
    { id: 'vid-gen-alibaba', pipeline: 'VideoGenerator', work: 'video', provider: 'alibaba', field: 'alibabaVideoApiKey', keywords: ['video', 'generation', 'alibaba', 'gen'], description: 'Video generation via Alibaba' },

    // ── Social Text ──
    { id: 'social-text-openai', pipeline: 'Social', work: 'text', provider: 'openai', field: 'openAiTextApiKey', keywords: ['social', 'text', 'openai', 'gpt', 'post'], description: 'Social post text via OpenAI' },
    { id: 'social-text-gemini', pipeline: 'Social', work: 'text', provider: 'gemini', field: 'geminiTextApiKey', keywords: ['social', 'text', 'gemini', 'google'], description: 'Social post text via Gemini' },
    { id: 'social-text-openrouter', pipeline: 'Social', work: 'text', provider: 'openrouter', field: 'openRouterTextApiKey', keywords: ['social', 'text', 'openrouter'], description: 'Social post text via OpenRouter' },
    { id: 'social-text-deepseek', pipeline: 'Social', work: 'text', provider: 'deepseek', field: 'deepSeekTextApiKey', keywords: ['social', 'text', 'deepseek'], description: 'Social post text via DeepSeek' },
    { id: 'social-text-alibaba', pipeline: 'Social', work: 'text', provider: 'alibaba', field: 'alibabaTextApiKey', keywords: ['social', 'text', 'alibaba', 'qwen'], description: 'Social post text via Alibaba' },

    // ── Social Image ──
    { id: 'social-image-openai', pipeline: 'Social', work: 'image', provider: 'openai', field: 'openAiImageApiKey', keywords: ['social', 'image', 'openai', 'dalle'], description: 'Social image via OpenAI DALL-E' },
    { id: 'social-image-gemini', pipeline: 'Social', work: 'image', provider: 'gemini', field: 'geminiImageApiKey', keywords: ['social', 'image', 'gemini', 'imagen'], description: 'Social image via Gemini' },
    { id: 'social-image-stability', pipeline: 'Social', work: 'image', provider: 'stability', field: 'stabilityImageApiKey', keywords: ['social', 'image', 'stability'], description: 'Social image via Stability AI' },

    // ── Social Video ──
    { id: 'social-video-alibaba', pipeline: 'Social', work: 'video', provider: 'alibaba', field: 'alibabaVideoApiKey', keywords: ['social', 'video', 'alibaba'], description: 'Social video via Alibaba' },

    // ── Social TTS ──
    { id: 'social-tts-openai', pipeline: 'Social', work: 'tts', provider: 'openai', field: 'openAiTtsApiKey', keywords: ['social', 'tts', 'openai', 'voice'], description: 'Social TTS via OpenAI' },
    { id: 'social-tts-azure', pipeline: 'Social', work: 'tts', provider: 'azure', field: 'azureTtsApiKey', keywords: ['social', 'tts', 'azure'], description: 'Social TTS via Azure' },
    { id: 'social-tts-elevenlabs', pipeline: 'Social', work: 'tts', provider: 'elevenlabs', field: 'elevenLabsTtsApiKey', keywords: ['social', 'tts', 'elevenlabs'], description: 'Social TTS via ElevenLabs' },

    // ── Social Whisper ──
    { id: 'social-whisper', pipeline: 'Social', work: 'whisper', provider: 'whisper', field: 'whisperApiKey', keywords: ['social', 'whisper', 'speech', 'stt'], description: 'Social speech-to-text via Whisper' },
    { id: 'social-groq-whisper', pipeline: 'Social', work: 'whisper', provider: 'groq', field: 'groqApiKey', keywords: ['social', 'groq', 'whisper'], description: 'Social speech-to-text via Groq' },
  ]);

  matchCommand(pipeline: string, work: string, provider: string): AiTestCommand | null {
    const pipe = pipeline.toLowerCase();
    const wrk = work.toLowerCase();
    const prov = provider.toLowerCase();
    return this.commands().find(c =>
      c.pipeline.toLowerCase() === pipe &&
      c.work.toLowerCase() === wrk &&
      c.provider.toLowerCase() === prov
    ) ?? null;
  }

  interpretNaturalLanguage(text: string): { pipeline: string; work: string; provider: string } | null {
    const lower = text.toLowerCase();

    // Detect pipeline
    const pipelineMap: { keywords: string[]; name: string }[] = [
      { keywords: ['processing', 'process'], name: 'Processing' },
      { keywords: ['trend', 'trending'], name: 'Trend' },
      { keywords: ['social', 'post', 'publish'], name: 'Social' },
      { keywords: ['video', 'smart video', 'generator', 'vid'], name: 'VideoGenerator' },
    ];

    // Detect work type
    const workMap: { keywords: string[]; name: string }[] = [
      { keywords: ['text', 'chat', 'script', 'content', 'write', 'likhay', 'تحریر', 'متن', 'لکھنا'], name: 'text' },
      { keywords: ['image', 'photo', 'picture', 'dalle', 'تصویر', 'فوٹو'], name: 'image' },
      { keywords: ['whisper', 'speech', 'stt', 'audio', 'transcribe', 'voice', 'آواز', 'بول', 'سننا', 'transcription'], name: 'whisper' },
      { keywords: ['tts', 'voice over', 'narration', 'speak', 'bolna', 'بولنا'], name: 'tts' },
      { keywords: ['music', 'sound', 'audio bg', 'song', 'گانا'], name: 'music' },
      { keywords: ['video', 'gen', 'generate', 'move', 'motion'], name: 'video' },
      { keywords: ['asset', 'stock', 'media', 'cloud', 'storage', 'hosting'], name: 'assets' },
    ];

    // Detect provider
    const providerMap: { keywords: string[]; name: string }[] = [
      { keywords: ['openai', 'gpt', 'chatgpt', 'dalle', 'whisper'], name: 'openai' },
      { keywords: ['openrouter', 'router'], name: 'openrouter' },
      { keywords: ['gemini', 'google'], name: 'gemini' },
      { keywords: ['deepseek', 'deep seek'], name: 'deepseek' },
      { keywords: ['alibaba', 'qwen', 'alib baba'], name: 'alibaba' },
      { keywords: ['groq'], name: 'groq' },
      { keywords: ['stability', 'stable diffusion', 'stable'], name: 'stability' },
      { keywords: ['leonardo'], name: 'leonardo' },
      { keywords: ['together', 'together ai'], name: 'together' },
      { keywords: ['azure', 'microsoft'], name: 'azure' },
      { keywords: ['elevenlabs', 'eleven', '11labs'], name: 'elevenlabs' },
      { keywords: ['cartesia'], name: 'cartesia' },
      { keywords: ['pexels'], name: 'pexels' },
      { keywords: ['puter'], name: 'puter' },
    ];

    const pipeline = this.findMatch(lower, pipelineMap);
    const work = this.findMatch(lower, workMap);
    const provider = this.findMatch(lower, providerMap);

    if (pipeline && work && provider) {
      return { pipeline, work, provider };
    }
    // If only pipeline + work but no specific provider, pick first match
    if (pipeline && work) {
      const first = this.commands().find(c =>
        c.pipeline === pipeline && c.work === work
      );
      if (first) return { pipeline, work, provider: first.provider };
    }
    return null;
  }

  private findMatch(text: string, map: { keywords: string[]; name: string }[]): string | null {
    for (const entry of map) {
      for (const kw of entry.keywords) {
        if (text.includes(kw)) return entry.name;
      }
    }
    return null;
  }

  testProviderKey(pipeline: string, field: string): Promise<TestProviderKeyResult> {
    this.isTesting.set(true);
    return new Promise((resolve) => {
      this.http.post<TestProviderKeyResult>(`${this.baseUrl}/brain/test-provider-key`, { pipeline, field })
        .subscribe({
          next: (res) => {
            this.lastResult.set({ command: `${pipeline}.${field}`, ...res });
            this.isTesting.set(false);
            resolve(res);
          },
          error: (err) => {
            const result = { success: false, message: err.error?.message || err.message || 'Connection failed' };
            this.lastResult.set({ command: `${pipeline}.${field}`, ...result });
            this.isTesting.set(false);
            resolve(result);
          }
        });
    });
  }

  async testFromVoice(text: string): Promise<string> {
    const parsed = this.interpretNaturalLanguage(text);
    if (!parsed) {
      return 'Sorry, I could not understand which AI provider to test. Try saying "test processing text openai" or "processing whisper test करें".';
    }

    const command = this.matchCommand(parsed.pipeline, parsed.work, parsed.provider);
    if (!command) {
      return `Found ${parsed.pipeline} ${parsed.work} but no exact provider match for "${parsed.provider}". Try a different provider name.`;
    }

    const result = await this.testProviderKey(command.pipeline, command.field);

    if (result.success) {
      return `✅ "${command.pipeline} ${command.work} via ${command.provider}" is working perfectly! ${command.description} is connected. बिलकुल सही काम कर रहा है!`;
    } else {
      return `❌ "${command.pipeline} ${command.work} via ${command.provider}" is NOT working. ${result.message}. Please check your API key in settings.`;
    }
  }
}
